// ============================================================================
// AccountPage — route /app/account  (namespace: "account")
// ----------------------------------------------------------------------------
// The trainer's self-service profile + settings, FULLY wired to the real API:
//   • GET  /api/account  → useAccount()        (current profile)
//   • PATCH /api/account → useUpdateAccount()   (display name / locale / currency
//                                                / password change)
// Two grouped Pulse Cards — Profile and Password — sit on the light canvas,
// mobile-first (single column at 390, two-up from lg). Data flows through the
// typed React Query hooks; on success the hook writes qk.account.detail and
// invalidates qk.auth.me, so the header name and the rest of the app reflect the
// change with no reload (live-sync). Changing language applies INSTANTLY via the
// i18n setLocale() (same path the LanguageToggle uses) and is persisted on save.
//
// Optional-first: only fields the trainer actually edits are sent. The password
// card is independent — it validates current+new+confirm client-side before the
// PATCH and maps an auth failure back to an inline "wrong current password".
//
// Routing + i18n are pre-wired (see app/router.tsx, i18n/config.ts); this file
// stays a DEFAULT export named AccountPage and owns the "account" namespace.
// Built on the LOCKED Pulse components — none of them are restyled here.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { Transition } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Badge,
  Banner,
  Button,
  Card,
  CardHeader,
  SelectField,
  TextField,
} from "../../components";
import type { SelectOption } from "../../components";
import { ApiError } from "../../api";
import type { AccountUpdate, Trainer } from "../../api";
import {
  useAccount,
  useSendEmailVerification,
  useUpdateAccount,
} from "../../hooks";
import { TrainerCalendarSync } from "../../app/calendar/TrainerCalendarSync";
import { SUPPORTED_LOCALES, LOCALE_LABELS, setLocale } from "../../i18n/config";
import type { Locale } from "../../i18n/config";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import { SUPPORTED_CURRENCIES } from "../../lib/currency";
import { deviceZone } from "../../lib/datetime";

const MIN_PASSWORD = 8;

/**
 * Currencies offered in the picker — limited to the three the backend bills in
 * (PATCH /api/account validates against db.SUPPORTED_CURRENCIES). A stored value
 * outside this set is still merged in so it stays selectable until changed.
 */
const CURRENCY_CODES = SUPPORTED_CURRENCIES;

const EASE = [0.22, 0.61, 0.36, 1] as const;

export default function AccountPage() {
  const { t } = useTranslation("account");
  const reduce = useReducedMotion();
  const account = useAccount();

  const enter = (i: number): Transition => ({
    duration: 0.45,
    delay: reduce ? 0 : 0.06 * (i + 1),
    ease: EASE,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <p className="text-sm font-bold uppercase tracking-kicker text-coral">
          {t("kicker")}
        </p>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body sm:text-4xl">
          {t("title")}
        </h1>
        <p className="mt-1 max-w-prose text-body-soft">{t("subtitle")}</p>
      </motion.div>

      {account.isError ? (
        <ErrorBlock onRetry={() => void account.refetch()} />
      ) : account.isLoading || !account.data ? (
        <LoadingBlock />
      ) : (
        <div className="space-y-6">
          {/* Wave 7: a doorway to the public marketing profile editor. */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={enter(0)}
          >
            <PublicProfileLinkCard
              isPublic={!!account.data.public_enabled}
              handle={account.data.handle ?? null}
            />
          </motion.div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={enter(0)}
            >
              <ProfileCard
                username={account.data.username}
                displayName={account.data.display_name}
                locale={account.data.locale}
                currency={account.data.default_currency}
                timezone={account.data.timezone ?? null}
                reduce={!!reduce}
              />
            </motion.div>

            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={enter(1)}
            >
              <PasswordCard reduce={!!reduce} />
            </motion.div>
          </div>

          {/* Email + verification (Wave 6). */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={enter(2)}
          >
            <EmailCard account={account.data} reduce={!!reduce} />
          </motion.div>

          {/* Sync to your calendar — the trainer's read-only ICS feed URL. */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={enter(3)}
          >
            <TrainerCalendarSync />
          </motion.div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Public-profile doorway — a feature card linking to the Wave 7 marketing
// profile editor (/app/account/profile), with a live/hidden status pill.
// ----------------------------------------------------------------------------
function PublicProfileLinkCard({
  isPublic,
  handle,
}: {
  isPublic: boolean;
  handle: string | null;
}) {
  const { t } = useTranslation("account");
  return (
    <Card className="space-y-4">
      <CardHeader
        title={t("publicProfile.title")}
        subtitle={t("publicProfile.subtitle")}
        action={
          <Badge tone={isPublic ? "success" : "neutral"} dot>
            {isPublic ? t("publicProfile.live") : t("publicProfile.hidden")}
          </Badge>
        }
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-body-soft">
          {handle
            ? t("publicProfile.handleNote", { handle })
            : t("publicProfile.noHandleNote")}
        </p>
        <Link to="/app/account/profile" className="w-full sm:w-auto">
          <Button size="sm" className="w-full sm:w-auto">
            {t("publicProfile.editCta")}
          </Button>
        </Link>
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// A success / error feedback line that animates in and out under reduced-motion.
// ----------------------------------------------------------------------------
function FeedbackSlot({
  tone,
  message,
  reduce,
}: {
  tone: "success" | "danger";
  message: string | null;
  reduce: boolean;
}) {
  return (
    <AnimatePresence initial={false}>
      {message && (
        <motion.div
          key={message}
          initial={reduce ? false : { opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: "auto", marginTop: 16 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.28, ease: EASE }}
          style={{ overflow: "hidden" }}
        >
          <Banner tone={tone}>{message}</Banner>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ----------------------------------------------------------------------------
// Profile card — display name, language, default currency. Optional-first:
// only changed fields are PATCHed. Language applies instantly (setLocale) and
// is persisted on save.
// ----------------------------------------------------------------------------
function ProfileCard({
  username,
  displayName,
  locale,
  currency,
  timezone,
  reduce,
}: {
  username: string;
  displayName: string | null;
  locale: string | null;
  currency: string | null;
  timezone: string | null;
  reduce: boolean;
}) {
  const { t } = useTranslation("account");
  const update = useUpdateAccount();

  const initialLocale = useMemo<Locale>(() => {
    const base = (locale ?? "en").split("-")[0];
    return (SUPPORTED_LOCALES as readonly string[]).includes(base)
      ? (base as Locale)
      : "en";
  }, [locale]);

  const [name, setName] = useState(displayName ?? "");
  const [lang, setLang] = useState<Locale>(initialLocale);
  const [curr, setCurr] = useState(currency ?? "");
  const [tz, setTz] = useState(timezone ?? "");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the form in sync when the underlying account changes (e.g. a live-sync
  // refetch from another device) — but never clobber edits the trainer is
  // mid-way through (the mutation result already matches server state on save).
  useEffect(() => {
    setName(displayName ?? "");
  }, [displayName]);
  useEffect(() => {
    setLang(initialLocale);
  }, [initialLocale]);
  useEffect(() => {
    setCurr(currency ?? "");
  }, [currency]);
  useEffect(() => {
    setTz(timezone ?? "");
  }, [timezone]);

  const currencyOptions: SelectOption[] = useMemo(() => {
    const codes = [...CURRENCY_CODES];
    if (currency && !codes.includes(currency as (typeof CURRENCY_CODES)[number])) {
      codes.unshift(currency as (typeof CURRENCY_CODES)[number]);
    }
    return codes.map((code) => ({
      value: code,
      label: t(`currencies.${code}`, code),
    }));
  }, [currency, t]);

  const tzOptions: SelectOption[] = useMemo(() => {
    let zones: string[];
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      zones = [...(Intl as any).supportedValuesOf("timeZone")] as string[];
    } catch {
      zones = [
        "Asia/Hong_Kong",
        "Asia/Shanghai",
        "Asia/Singapore",
        "Europe/London",
        "America/New_York",
        "Australia/Sydney",
      ];
    }
    const dz = deviceZone();
    if (!zones.includes(dz)) zones = [dz, ...zones];
    return zones.map((z) => ({ value: z, label: z }));
  }, []);

  // The diff that would be sent. Empty → nothing to save.
  const patch = useMemo<AccountUpdate>(() => {
    const next: AccountUpdate = {};
    const trimmedName = name.trim();
    if (trimmedName !== (displayName ?? "")) {
      next.display_name = trimmedName === "" ? null : trimmedName;
    }
    if (lang !== initialLocale) next.locale = lang;
    if (curr !== (currency ?? "")) {
      next.default_currency = curr === "" ? null : curr;
    }
    if (tz !== (timezone ?? "")) {
      next.timezone = tz === "" ? null : tz;
    }
    return next;
  }, [name, lang, curr, tz, displayName, initialLocale, currency, timezone]);

  const dirty = Object.keys(patch).length > 0;

  // Apply the language live the moment it changes, so the whole UI reacts even
  // before the save round-trips (mirrors the header LanguageToggle behaviour).
  const onLanguage = (value: string) => {
    const next = value as Locale;
    setLang(next);
    setSaved(false);
    setError(null);
    setLocale(next);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || update.isPending) return;
    setError(null);
    setSaved(false);
    update.mutate(patch, {
      onSuccess: (trainer) => {
        setSaved(true);
        // Re-anchor the live locale to whatever the server persisted.
        const base = (trainer.locale ?? "en").split("-")[0];
        if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) {
          setLocale(base as Locale);
        }
      },
      onError: () => setError(t("feedback.saveFailed")),
    });
  };

  return (
    <Card className="space-y-5">
      <CardHeader title={t("profile.title")} subtitle={t("profile.subtitle")} />

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <TextField
          label={t("profile.username")}
          value={username}
          hint={t("profile.usernameHint")}
          disabled
          readOnly
          autoComplete="username"
        />

        <TextField
          label={t("profile.displayName")}
          value={name}
          placeholder={t("profile.displayNamePlaceholder")}
          hint={t("profile.displayNameHint")}
          autoComplete="name"
          onChange={(e) => {
            setName(e.target.value);
            setSaved(false);
            setError(null);
          }}
        />

        <SelectField
          label={t("profile.language")}
          value={lang}
          hint={t("profile.languageHint")}
          options={SUPPORTED_LOCALES.map((loc) => ({
            value: loc,
            label: LOCALE_LABELS[loc],
          }))}
          onChange={(e) => onLanguage(e.target.value)}
        />

        <SelectField
          label={t("profile.currency")}
          value={curr}
          hint={t("profile.currencyHint")}
          placeholder="—"
          options={currencyOptions}
          onChange={(e) => {
            setCurr(e.target.value);
            setSaved(false);
            setError(null);
          }}
        />

        <SelectField
          label={t("profile.timezone")}
          value={tz}
          hint={t("profile.timezoneHint")}
          placeholder="—"
          options={tzOptions}
          onChange={(e) => {
            setTz(e.target.value);
            setSaved(false);
            setError(null);
          }}
        />

        <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
          <AnimatePresence initial={false} mode="wait">
            {dirty ? (
              <motion.span
                key="dirty"
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="order-2 text-sm text-body-faint sm:order-1"
              >
                {t("profile.noChanges")}
              </motion.span>
            ) : (
              <span key="clean" className="order-2 sm:order-1" aria-hidden />
            )}
          </AnimatePresence>

          <Button
            type="submit"
            className="order-1 w-full sm:order-2 sm:w-auto"
            disabled={!dirty || update.isPending}
            aria-disabled={!dirty || update.isPending}
          >
            {update.isPending ? t("profile.saving") : t("profile.save")}
          </Button>
        </div>
      </form>

      <FeedbackSlot
        tone="success"
        message={saved ? t("profile.saved") : null}
        reduce={reduce}
      />
      <FeedbackSlot tone="danger" message={error} reduce={reduce} />
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Password card — current + new + confirm, validated client-side. Sends only
// the password fields; an auth failure maps to a "wrong current password"
// inline error.
// ----------------------------------------------------------------------------
function PasswordCard({ reduce }: { reduce: boolean }) {
  const { t } = useTranslation("account");
  const update = useUpdateAccount();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState(false);
  const currentRef = useRef<HTMLInputElement>(null);

  // Pure validation — recomputed each render; only surfaced once `touched`.
  const errors = useMemo(() => {
    const e: { current?: string; next?: string; confirm?: string } = {};
    if (!current) e.current = t("password.errors.currentRequired");
    if (!next) e.next = t("password.errors.newRequired");
    else if (next.length < MIN_PASSWORD) e.next = t("password.errors.tooShort");
    else if (next === current) e.next = t("password.errors.sameAsOld");
    if (!confirm) e.confirm = t("password.errors.confirmRequired");
    else if (next !== confirm) e.confirm = t("password.errors.mismatch");
    return e;
  }, [current, next, confirm, t]);

  // A server-rejected current password (401/403/400) shown on the current field.
  const serverCurrentError =
    update.isError &&
    update.error instanceof ApiError &&
    [400, 401, 403].includes(update.error.status)
      ? t("password.errors.wrongCurrent")
      : undefined;

  const valid = Object.keys(errors).length === 0;

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setTouched(false);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setDone(false);
    if (!valid || update.isPending) {
      if (!valid) currentRef.current?.focus();
      return;
    }
    update.reset();
    update.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          setDone(true);
          reset();
        },
      },
    );
  };

  const onAnyChange = () => {
    if (done) setDone(false);
    if (update.isError) update.reset();
  };

  const showErr = (k: keyof typeof errors) => (touched ? errors[k] : undefined);

  return (
    <Card className="space-y-5">
      <CardHeader
        title={t("password.title")}
        subtitle={t("password.subtitle")}
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShow((s) => !s)}
            aria-pressed={show}
          >
            {show ? t("password.hide") : t("password.show")}
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <TextField
          ref={currentRef}
          label={t("password.current")}
          type={show ? "text" : "password"}
          value={current}
          placeholder={t("password.currentPlaceholder")}
          autoComplete="current-password"
          error={showErr("current") ?? serverCurrentError}
          onChange={(e) => {
            setCurrent(e.target.value);
            onAnyChange();
          }}
        />

        <TextField
          label={t("password.new")}
          type={show ? "text" : "password"}
          value={next}
          placeholder={t("password.newPlaceholder")}
          autoComplete="new-password"
          hint={t("password.strength")}
          error={showErr("next")}
          onChange={(e) => {
            setNext(e.target.value);
            onAnyChange();
          }}
        />

        <TextField
          label={t("password.confirm")}
          type={show ? "text" : "password"}
          value={confirm}
          placeholder={t("password.confirmPlaceholder")}
          autoComplete="new-password"
          error={showErr("confirm")}
          onChange={(e) => {
            setConfirm(e.target.value);
            onAnyChange();
          }}
        />

        <div className="flex items-center justify-between gap-3 pt-1">
          <AnimatePresence initial={false}>
            {done && (
              <motion.span
                key="pw-done"
                initial={reduce ? false : { opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: EASE }}
              >
                <Badge tone="success" dot>
                  {t("password.updated")}
                </Badge>
              </motion.span>
            )}
          </AnimatePresence>

          <Button
            type="submit"
            variant="secondary"
            className="ml-auto w-full sm:w-auto"
            disabled={update.isPending}
            aria-disabled={update.isPending}
          >
            {update.isPending ? t("password.updating") : t("password.update")}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Email card — add/confirm the trainer's contact email and verify it (Wave 6).
//   • The email field PATCHes /api/account; changing it RESETS verification
//     server-side, so the verified badge clears the moment a new address saves.
//   • When an email is on file but unverified, a "Send verification email"
//     button POSTs /api/account/email/verify/send; the emailed link lands on
//     /account/verify-email and flips this card to the verified state.
//   • A verified email shows a success Badge and no action.
// Optional-first: the email is only sent when it actually changed.
// ----------------------------------------------------------------------------
function EmailCard({ account, reduce }: { account: Trainer; reduce: boolean }) {
  const { t } = useTranslation("account");
  const update = useUpdateAccount();
  const sendVerify = useSendEmailVerification();

  const storedEmail = account.email ?? "";
  const verified = !!account.email_verified;

  const [email, setEmail] = useState(storedEmail);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sentFeedback, setSentFeedback] = useState<string | null>(null);

  // Re-sync from the server when the underlying account changes (live-sync /
  // a save round-trip) without clobbering an in-progress edit.
  useEffect(() => {
    setEmail(storedEmail);
  }, [storedEmail]);

  const trimmed = email.trim();
  const dirty = trimmed !== storedEmail;
  // Loose client-side email shape check (server is the source of truth).
  const looksValid = trimmed === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || update.isPending) return;
    if (!looksValid) {
      setSaveError(t("email.invalid"));
      return;
    }
    setSaveError(null);
    setSaved(false);
    setSentFeedback(null);
    update.mutate(
      { email: trimmed === "" ? null : trimmed },
      {
        onSuccess: () => setSaved(true),
        onError: () => setSaveError(t("feedback.saveFailed")),
      },
    );
  };

  const onSend = () => {
    if (sendVerify.isPending) return;
    setSentFeedback(null);
    sendVerify.mutate(undefined, {
      onSuccess: (res) => {
        if (res.already_verified) setSentFeedback(t("email.alreadyVerified"));
        else if (res.email_sent) setSentFeedback(t("email.sent"));
        else setSentFeedback(t("email.sentDev"));
      },
      onError: () => setSentFeedback(t("email.sendFailed")),
    });
  };

  return (
    <Card className="space-y-5">
      <CardHeader
        title={t("email.title")}
        subtitle={t("email.subtitle")}
        action={
          verified ? (
            <Badge tone="success" dot>
              {t("email.verified")}
            </Badge>
          ) : storedEmail ? (
            <Badge tone="warning" dot>
              {t("email.unverified")}
            </Badge>
          ) : (
            <Badge tone="neutral">{t("email.none")}</Badge>
          )
        }
      />

      <form className="space-y-4" onSubmit={onSave} noValidate>
        <TextField
          label={t("email.address")}
          type="email"
          inputMode="email"
          value={email}
          placeholder={t("email.placeholder")}
          hint={t("email.hint")}
          autoComplete="email"
          error={!looksValid && dirty ? t("email.invalid") : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            setSaved(false);
            setSaveError(null);
          }}
        />

        <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
          <span className="order-2 text-sm text-body-faint sm:order-1">
            {dirty ? t("email.changeNote") : ""}
          </span>
          <Button
            type="submit"
            variant="secondary"
            className="order-1 w-full sm:order-2 sm:w-auto"
            disabled={!dirty || update.isPending}
            aria-disabled={!dirty || update.isPending}
          >
            {update.isPending ? t("email.saving") : t("email.save")}
          </Button>
        </div>
      </form>

      {/* Verify action — only when an email is on file and not yet verified
          (and not mid-edit, so the trainer saves the new address first). */}
      {storedEmail && !verified && !dirty && (
        <div className="rounded-md bg-amber-soft p-4">
          <p className="font-display text-sm font-bold text-body">
            {t("email.verifyTitle")}
          </p>
          <p className="mt-0.5 text-sm text-body-soft">{t("email.verifyBlurb")}</p>
          <Button
            size="sm"
            className="mt-3 w-full sm:w-auto"
            onClick={onSend}
            disabled={sendVerify.isPending}
            aria-disabled={sendVerify.isPending}
          >
            {sendVerify.isPending ? t("email.sending") : t("email.sendCta")}
          </Button>
        </div>
      )}

      <FeedbackSlot
        tone="success"
        message={saved ? t("email.saved") : null}
        reduce={reduce}
      />
      <FeedbackSlot tone="danger" message={saveError} reduce={reduce} />
      <FeedbackSlot
        tone={sendVerify.isError ? "danger" : "success"}
        message={sentFeedback}
        reduce={reduce}
      />
    </Card>
  );
}
